/**
 * Processes slug-like strings with dynamic token replacement and advanced formatting options.
 *
 * Supports various token formats:
 * - Basic replacement: {fieldName} -> data.fieldName
 * - Nested properties: {user.profile.name} -> data.user.profile.name
 * - Substring extraction: {fieldName:0:5} -> first 5 characters
 * - Default values: {fieldName|defaultValue} -> uses defaultValue if fieldName is null/undefined
 * - Combined: {user.name:0:10|Anonymous} -> first 10 chars of user.name or "Anonymous"
 *
 * @param sluggable - The template string containing tokens to replace
 * @param data - The data object containing values for token replacement
 * @returns The processed string with all tokens replaced
 *
 * @example
 * processSlugs("Hello {user.name:0:5|Guest}!", { user: { name: "John Doe" } })
 * // Returns: "Hello John!"
 *
 * processSlugs("ID: {id|N/A}", { id: null })
 * // Returns: "ID: N/A"
 */
export function processSlugs(sluggable: string, data: Indexable): string {
  const tokenPattern = /\{([^}]+)\}/g;

  if (!sluggable.includes("{")) {
    return sluggable;
  }

  let replacedString: string = sluggable.replace(
    tokenPattern,
    (match, token) => {
      // Split by pipe to separate field path from default value
      const [fieldPart, defaultValue = ""] = token.split("|");

      // Split by colon to separate field path from substring parameters
      const [fieldPath, startIndex, endIndex] = fieldPart.trim().split(":");

      // Navigate through nested object properties
      const replacement = fieldPath
        .trim()
        .split(".")
        .reduce((a: any, b: string) => {
          if (a && typeof a === "object" && b in a) {
            return (a as Record<string, unknown>)[b];
          }
          return undefined;
        }, data);

      // If value exists, apply substring if specified
      if (
        replacement !== undefined &&
        replacement !== null &&
        replacement !== ""
      ) {
        const stringValue = String(replacement);

        // Apply substring extraction if parameters provided
        if (startIndex !== undefined) {
          const start = parseInt(startIndex) || 0;
          const end = endIndex !== undefined ? parseInt(endIndex) : undefined;
          return stringValue.substring(start, end);
        }

        return stringValue;
      }

      // Use default value if original value is null/undefined
      return defaultValue;
    }
  );

  return replacedString;
}

/**
 * Removes all slug tokens from a string.
 *
 * @param sluggable - The string containing slug tokens to remove
 * @returns The string with all slug tokens removed
 *
 * @example
 * stripSlugs("Hello {user.name}!")
 * // Returns: "Hello !"
 */
export function stripSlugs(sluggable: string): string {
  return sluggable.replace(/\{[^}]+\}/g, "").trim();
}
